/**
 * WHICH WAY THE READER IS READING — the one place the browser is asked.
 *
 * `I18nProvider` writes `dir` on `document.documentElement`, because the
 * attribute has to reach scrollbars, form controls and the browser's own text
 * selection. Everything that mirrors then has to ask what it says: a dropped
 * panel's start edge, a wheel that scrolls a row, a drag whose travel adds one
 * way and subtracts the other, the arrow key that means "onward".
 *
 * It was asked in five places and one of them was asking a DIFFERENT QUESTION.
 * Four read the document; the fifth read `getComputedStyle(element).direction`,
 * which is the direction in force at one node and answers differently inside a
 * subtree that sets its own. Nothing said which of the two each caller meant,
 * so the pair could not be told apart by reading the code — the reason both
 * questions are named here rather than spelled at the call sites.
 *
 * A COMPONENT INSIDE THE PROVIDER ASKS THE PROVIDER. `useI18n().direction` is
 * the same fact from the same writer, and it re-renders when the workspace
 * language changes, which a function call in a render cannot. These are for the
 * places a hook cannot reach: an event handler, a placement helper, a module
 * with no component around it.
 */

/**
 * The direction the DOCUMENT is being read in — the workspace language's, for
 * every surface in it.
 */
export function readingDirectionIsRtl(): boolean {
  return document.documentElement.dir === 'rtl'
}

/**
 * The direction in force AT ONE ELEMENT, which is not the same question.
 *
 * It answers the document's direction for almost every node and something else
 * inside a subtree that states its own — a `dir=auto` field holding Hebrew in a
 * Latin workspace, a quoted passage, a mirrored preview. Ask it only where the
 * element's own reading is what is being measured; ask the document otherwise,
 * because a control that happens to sit inside such a subtree would otherwise
 * mirror against the page around it.
 */
export function elementDirectionIsRtl(element: Element): boolean {
  return getComputedStyle(element).direction === 'rtl'
}
